import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { TenantThemeProvider } from "./context/TenantThemeContext";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import HullScanUnlocked from "./pages/HullScanUnlocked";
import Login from "./pages/Login";
import Register from "./pages/Register";
import TerritoryMap from "./pages/TerritoryMap";
import BoardingDeck from "./pages/BoardingDeck";
import BoardingSuccess from "./pages/BoardingSuccess";
import FuelSuccess from "./pages/FuelSuccess";
import CatalogThankYou from "./pages/CatalogThankYou";
import Dashboard from "./pages/Dashboard";
import ScoutReports from "./pages/ScoutReports";
import AdKitRepository from "./pages/AdKitRepository";
import Subdomains from "./pages/Subdomains";
import DirectorPanel from "./pages/DirectorPanel";
import DirectorBoarding from "./pages/DirectorBoarding";
import DirectorHardware from "./pages/DirectorHardware";
import DirectorForensics from "./pages/DirectorForensics";
import DirectorLeads from "./pages/DirectorLeads";
import TenantHome from "./components/TenantHome";
import Bridge from "./pages/Bridge";
import CommissionExchange from "./pages/CommissionExchange";
import SportsCmdr from "./pages/SportsCmdr";
import TdCmdr from "./pages/TdCmdr";
import Privateer from "./pages/Privateer";
import ComplianceFooter from "./components/ComplianceFooter";
import IntelHub from "./pages/IntelHub";
import IntelPost from "./pages/IntelPost";
import FieldOperative from "./pages/FieldOperative";
import MissionControl from "./pages/MissionControl";
import FoundryListing from "./pages/FoundryListing";
import Foundry from "./pages/Foundry";
import SolanaPortal from "./pages/SolanaPortal";
import RemoteSupport from "./pages/RemoteSupport";
import MagicLinkLand from "./pages/MagicLinkLand";
import Partners from "./pages/Partners";
import PartnerGo from "./pages/PartnerGo";
import Catalog from "./pages/Catalog";
import About from "./pages/About";
import SocialGo from "./pages/SocialGo";
import SovereignDiagnostic from "./pages/SovereignDiagnostic";
import AuthCallback from "./pages/AuthCallback";
import FleetSignalBar from "./components/FleetSignalBar";
import FleetUpdateNotifier from "./components/FleetUpdateNotifier";
import SovereignAssistant from "./components/SovereignAssistant";
import { recordAuthBounce, isLoopTripped, resetAuthBounces } from "./lib/authLoopBreaker";

function ProtectedRoute({ children, directorOnly, sportsAdmiralOnly, tdCommanderOnly, privateerOnly, operativeOnly }) {
    const { user } = useAuth();
    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
                Awaiting clearance...
            </div>
        );
    }
    if (!user) {
        // Auth Loop-Breaker · Director directive 2026.02 (Strike #17).
        // If this redirect would be the Nth bounce inside a 30s window
        // (N defaults to 3), the user is in a cookie-rejection loop —
        // intercept and show the Sovereign Diagnostic page instead of
        // sending them back to /login for the third time.
        recordAuthBounce();
        if (isLoopTripped()) {
            return <Navigate to="/diagnostic" replace />;
        }
        return <Navigate to="/login" replace />;
    }
    // Reached an authenticated render — loop is broken, clear the tally.
    resetAuthBounces();
    // Directive · "Seal the Hull" + "Presidential Bypass" — Dontae Kirk
    // (director@eigoo.com AND public-facing sales@bizboogie.com) is
    // ALWAYS treated as the Director on the frontend, bypassing any
    // role-sync lag between preview and production Mongo instances. The
    // backend mirrors this in require_director.
    const DIRECTOR_EMAILS = new Set(["director@eigoo.com", "sales@bizboogie.com"]);
    const isDirectorByEmail = DIRECTOR_EMAILS.has((user?.email || "").toLowerCase());
    const effectiveRole = isDirectorByEmail ? "director" : user.role;
    if (directorOnly && effectiveRole !== "director") return <Navigate to="/app/map" replace />;
    if (sportsAdmiralOnly && effectiveRole !== "sports_admiral" && effectiveRole !== "director")
        return <Navigate to="/app/map" replace />;
    if (tdCommanderOnly && effectiveRole !== "td_commander" && effectiveRole !== "director")
        return <Navigate to="/app/map" replace />;
    if (privateerOnly && effectiveRole !== "founding_privateer" && effectiveRole !== "director")
        return <Navigate to="/app/map" replace />;
    if (operativeOnly && effectiveRole !== "field_operative" && effectiveRole !== "director")
        return <Navigate to="/" replace />;
    return children;
}

function AppShellRoute({ children, directorOnly, sportsAdmiralOnly, tdCommanderOnly, privateerOnly }) {
    return (
        <ProtectedRoute
            directorOnly={directorOnly}
            sportsAdmiralOnly={sportsAdmiralOnly}
            tdCommanderOnly={tdCommanderOnly}
            privateerOnly={privateerOnly}
        >
            <Layout>{children}</Layout>
        </ProtectedRoute>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <TenantThemeProvider>
                    <Toaster
                        position="top-right"
                        theme="dark"
                        toastOptions={{
                            style: {
                                background: "hsl(0 0% 7%)",
                                border: "1px solid hsl(45 70% 47% / 0.4)",
                                color: "hsl(45 30% 96%)",
                            },
                        }}
                    />

                    {/* Unified Fleet Signal HUD — pinned above every vessel header */}
                    <FleetSignalBar />
                    <FleetUpdateNotifier />
                    {/* Sovereign Assistant — floating support bubble (Strike #23) */}
                    <SovereignAssistant />

                    <Routes>
                        <Route path="/" element={<TenantHome />} />
                        <Route path="/hull-scan/unlocked/:scan_id" element={<HullScanUnlocked />} />
                        <Route path="/intel" element={<IntelHub />} />
                        <Route path="/intel/:slug" element={<IntelPost />} />
                        <Route path="/foundry" element={<Foundry />} />
                        <Route path="/shipyard" element={<Foundry />} />
                        <Route path="/solana" element={<SolanaPortal />} />
                        <Route path="/remote" element={<RemoteSupport />} />
                        <Route path="/magic/:token" element={<MagicLinkLand />} />
                        <Route path="/partners" element={<Partners />} />
                        <Route path="/go/:slug" element={<PartnerGo />} />
                        <Route path="/catalog" element={<Catalog />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/vision" element={<About />} />
                        <Route path="/ig" element={<SocialGo />} />
                        <Route path="/fb" element={<SocialGo />} />
                        <Route path="/diagnostic" element={<SovereignDiagnostic />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        <Route path="/foundry/control" element={<MissionControl />} />
                        <Route path="/foundry/operative" element={<ProtectedRoute operativeOnly><FieldOperative /></ProtectedRoute>} />
                        <Route path="/property/foundry-listing-01" element={<FoundryListing />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/commission/:token" element={<CommissionExchange />} />
                        <Route path="/bridge" element={<Bridge />} />
                        <Route path="/boarding/success" element={<ProtectedRoute><BoardingSuccess /></ProtectedRoute>} />
                        <Route path="/fuel/success" element={<ProtectedRoute><FuelSuccess /></ProtectedRoute>} />
                        <Route path="/catalog/thank-you" element={<CatalogThankYou />} />
                        <Route path="/app/map" element={<AppShellRoute><TerritoryMap /></AppShellRoute>} />
                        <Route path="/app/boarding" element={<AppShellRoute><BoardingDeck /></AppShellRoute>} />
                        <Route path="/app/dashboard" element={<AppShellRoute><Dashboard /></AppShellRoute>} />
                        <Route path="/app/scout" element={<AppShellRoute><ScoutReports /></AppShellRoute>} />
                        <Route path="/app/ad-kits" element={<AppShellRoute><AdKitRepository /></AppShellRoute>} />
                        <Route path="/app/subdomains" element={<AppShellRoute><Subdomains /></AppShellRoute>} />
                        <Route path="/app/director" element={<AppShellRoute directorOnly><DirectorPanel /></AppShellRoute>} />
                        <Route path="/app/director/boarding" element={<AppShellRoute directorOnly><DirectorBoarding /></AppShellRoute>} />
                        <Route path="/app/director/hardware" element={<AppShellRoute directorOnly><DirectorHardware /></AppShellRoute>} />
                        <Route path="/app/director/forensics" element={<AppShellRoute directorOnly><DirectorForensics /></AppShellRoute>} />
                        <Route path="/app/leads" element={<AppShellRoute directorOnly><DirectorLeads /></AppShellRoute>} />
                        <Route path="/app/sports-cmdr" element={<ProtectedRoute sportsAdmiralOnly><SportsCmdr /></ProtectedRoute>} />
                        <Route path="/app/td-cmdr" element={<ProtectedRoute tdCommanderOnly><TdCmdr /></ProtectedRoute>} />
                        <Route path="/app/privateer" element={<ProtectedRoute privateerOnly><Privateer /></ProtectedRoute>} />
                        <Route path="/app" element={<Navigate to="/app/map" replace />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <ComplianceFooter />
                    </TenantThemeProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
